// Per-row action menu — RON-5469. Prototype region: `.js-row-action` + `#dd-row-actions`.
//
// The same actions the detail panel offers, reachable WITHOUT selecting the row first. At a busy
// desk, signing somebody out should not cost two clicks and a context switch.
//
// Only the actions that exist are rendered. The prototype's menu has seven entries; three of them
// have nothing behind them, and a menu item that only raises a toast is a promise the screen
// cannot keep:
//
//   row-print   badge printing is an integration item (§9)
//   row-edit    there is NO reception edit endpoint — the contract has nine routes, and none of
//               them edits a visit
//   row-block   `vcm.visitor.block` is a capability with no table behind it (§9)

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ReceptionRowMenu = ({ row, isBusy, onView, onCheckIn, onCheckOut, onNotifyHost, onToggleVip }) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => event.key === "Escape" && setIsOpen(false);

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const run = (action) => () => {
    setIsOpen(false);
    action?.(row);
  };

  return (
    <div className="reception-rowmenu" ref={wrapRef}>
      <button
        type="button"
        className="reception-rowmenu__trigger"
        // The row behind this is itself clickable; without this the menu would open AND select.
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((current) => !current);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("react_reception_row_actions").replace("%s", row.name)}
      >
        ⋯
      </button>

      {isOpen && (
        <div className="reception-rowmenu__menu" role="menu">
          <button type="button" role="menuitem" className="reception-rowmenu__item" onClick={run(onView)}>
            {t("react_reception_view_details")}
          </button>

          {/* Driven by the SERVER's flags, exactly as the detail panel is — not inferred from
              `status`, because the rules include things a row cannot show you (§4). Hidden here
              rather than greyed: a menu is a list of what you can do, and §4.5's "grey rather
              than hide" is about the persistent controls on the detail panel. */}
          {row.canCheckIn && (
            <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onCheckIn)}>
              {t("react_reception_check_in")}
            </button>
          )}

          {row.canCheckOut && (
            <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onCheckOut)}>
              {t("react_reception_check_out")}
            </button>
          )}

          <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onNotifyHost)}>
            {t("react_reception_notify_host")}
          </button>

          <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onToggleVip)}>
            {t(row.isVip ? "react_reception_remove_vip" : "react_reception_mark_vip")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReceptionRowMenu;
